// menu-controller.ts — the HTML/DOM general-UI menu, LOGIC half (HTML/DOM UI
// shell plan, Phase 2). Pure TypeScript, no DOM: a small screen state machine
// that enumerates the SAME mission data the canvas SelectMissionForm uses
// (missionpick.CreateMissionList) and launches a mission through a launch seam
// shaped exactly like the canvas shell's flow (ShellFlowDeps.playLevel) — so
// the DOM menu's launch BOUNDARY is directly comparable to the canvas oracle
// (T2 equivalence) and the whole controller is node-testable. The browser view
// (menu-view.ts) renders this state and routes clicks back to these methods.
//
// Phase 2 scope: main menu + single-player mission select + launch + return.
// options/help/load-save/multiplayer are added as more screens + flow methods
// in the same shape.

import {
  CreateMissionList,
  kmltSinglePlayer,
  kmtStory,
  kmtChallenge,
  type MissionListDeps,
} from '../ui/missionpick.ts';
import type { MissionIdentifier } from '../io/savegame.ts';
import type { Stream } from '../../../sim/src/sm/stream.ts';

export type MenuScreen = 'main' | 'play' | 'missions' | 'options' | 'help' | 'load';

/** A saved-game slot (a user save; the auto-resume slot is excluded upstream). */
export interface SaveRow {
  slot: number; // nGame
  name: string; // szName (level title)
  whenMs: number; // msDate
}

/** Saved-game enumeration + open seam — absent ⇒ LOAD SAVED GAME disabled. */
export interface MenuSaves {
  list(): SaveRow[];
  open(slot: number): Stream | null;
}

export type Difficulty = 'easy' | 'normal' | 'hard';

/** The shell options the menu can edit, abstracted from OptionsState/Preferences
 *  so the controller stays node-testable. The host backs these with the same
 *  OptionsState/sndm + SavePreferences the canvas options form writes. */
export interface MenuOptionsIO {
  difficulty(): Difficulty;
  setDifficulty(d: Difficulty): void;
  muted(): boolean;
  setMuted(f: boolean): void;
  /** Mouse Input — web no longer forces it (PR #124); the shell toggle persists the
   *  mouse_mode pref the next game boots from (touch-primary devices default OFF). */
  mouseMode(): boolean;
  setMouseMode(f: boolean): void;
  /** game-speed slider notch (0..14 over gatGameSpeeds) + its "N.Nx" label. */
  gameSpeedNotch(): number;
  setGameSpeedNotch(i: number): void;
  gameSpeedLabel(): string;
  gameSpeedNotchMax(): number;
  /** the canvas DEFAULT-button game-speed notch. */
  defaultGameSpeedNotch(): number;
  /** scroll-speed (1.0..5.0, .25 step) + its "N.Nx" label. */
  scrollSpeed(): number;
  setScrollSpeed(v: number): void;
  scrollSpeedLabel(): string;
  /** persist (the canvas form's savePreferences round-trip). */
  save(): void;
}

export interface MissionRow {
  /** position in missions() — pass back to launch() (entries may be skipped, so
   *  this is the row's slot in the list, not the underlying MissionList index). */
  index: number;
  lvl: string; // szLvlFilename (e.g. 's_00.lvl')
  packId: number; // packid.id >>> 0
  title: string; // [General] Title
  pack: string; // pack/bucket display name
  author: string; // the pack's author (community packs); empty for the main game
  bucket: 'story' | 'challenge' | 'addon';
  miid: MissionIdentifier;
}

/** The launch seam — the SAME shape as the canvas shell's ShellFlowDeps, so the
 *  DOM menu forwards into GameHost exactly as the canvas menu does (and a
 *  recording stub makes the boundary directly comparable in T2). */
export interface MenuFlow {
  playLevel(miid: MissionIdentifier, nRank: number, done: (nGo: number) => void): void;
  playSavedGame(pstm: Stream, done: (nGo: number) => void): void;
  /** start the DOM multiplayer lobby (Phase 3) — absent ⇒ MULTIPLAYER disabled. */
  playMultiplayer?(): void;
  /** open the DOM ADD-ON MISSION PACKS catalog (Phase 3) — absent ⇒ the
   *  ADD-ON MISSION PACKS entry is disabled. */
  showAddOnPacks?(): void;
  /** open the native DOM COMMUNITY screen (PLAYERS / GAMES / MY PROFILE tabs — replaces the
   *  separate LEADERBOARD and MY PROFILE entries). `tab` deep-links a specific tab. Absent ⇒
   *  the COMMUNITY entry is disabled. */
  showCommunity?(tab?: 'players' | 'games' | 'profile'): void;
}

export interface MenuControllerDeps {
  /** gpakr/gppackm/gppim — the SP mission enumeration source (main pack at least). */
  mlDeps: MissionListDeps;
  flow: MenuFlow;
  /** the view re-renders on every state change. */
  onChange: () => void;
  /** open an external URL (leaderboard/forums) — the browser supplies window.open. */
  openUrl?: (url: string) => void;
  /** options editing seam — absent ⇒ the OPTIONS entry is disabled. */
  options?: MenuOptionsIO;
  /** load the help text (help.txt, tags stripped) — absent ⇒ HELP disabled. The flat-text
   *  fallback the view shows when the full manual (loadHelpRaw) isn't supplied. */
  loadHelp?: () => string;
  /** load the RAW help.txt (tags intact) for the FULL-FIDELITY DOM manual (images / cross-
   *  links / Index / Back). Present ⇒ the view renders the rich HelpManualView; absent ⇒ the
   *  flat tag-stripped loadHelp text. */
  loadHelpRaw?: () => string;
  /** resolve a help <IMG SRC="file.anir"> to a PNG data URL (the pack's 'help'-strip frame),
   *  or null if it can't be resolved. Wired from pakr+tam via ClientAnimationData (the same
   *  path ui/help.ts's canvas renderer uses). Absent ⇒ the manual renders image alt text. */
  resolveHelpImage?: (src: string) => string | null;
  /** saved-game enumeration/open — absent ⇒ LOAD SAVED GAME disabled. */
  saves?: MenuSaves;
}

function bucketOf(mt: number): MissionRow['bucket'] {
  switch (mt) {
    case kmtStory:
      return 'story';
    case kmtChallenge:
      return 'challenge';
    default:
      return 'addon';
  }
}

export class MenuController {
  screen: MenuScreen = 'main';
  /** true while a launched game runs (the view hides; the canvas shows). */
  inGame = false;

  private rows: MissionRow[] = [];
  private helpText = '';
  private helpRawText = '';
  private savedRows: SaveRow[] = [];
  /** the options at OPTIONS-open, so CANCEL can revert the live edits
   *  (setters apply immediately, like the canvas mute checkbox). */
  private optionsSnapshot: {
    difficulty: Difficulty;
    muted: boolean;
    gameSpeed: number;
    scrollSpeed: number;
    mouseMode: boolean;
  } | null = null;
  private readonly deps: MenuControllerDeps;

  constructor(deps: MenuControllerDeps) {
    this.deps = deps;
  }

  missions(): readonly MissionRow[] {
    return this.rows;
  }

  /** PLAY → the play submenu (Single Player / Multiplayer), like the canvas
   *  kidfPlay form — not straight to the mission list. */
  play(): void {
    this.screen = 'play';
    this.deps.onChange();
  }

  /** Single Player → enumerate the SP missions and show the list. */
  singlePlayer(): void {
    this.rows = this.enumerateMissions();
    this.screen = 'missions';
    this.deps.onChange();
  }

  /** Multiplayer → hand off to the DOM lobby flow (Phase 3). */
  hasMultiplayer(): boolean {
    return this.deps.flow.playMultiplayer !== undefined;
  }
  multiplayer(): void {
    this.deps.flow.playMultiplayer?.();
  }

  /** ADD-ON MISSION PACKS → the DOM download catalog (Phase 3). */
  hasAddOnPacks(): boolean {
    return this.deps.flow.showAddOnPacks !== undefined;
  }
  showAddOnPacks(): void {
    this.deps.flow.showAddOnPacks?.();
  }

  /** COMMUNITY → the native DOM community screen (PLAYERS / GAMES / MY PROFILE tabs). */
  hasCommunity(): boolean {
    return this.deps.flow.showCommunity !== undefined;
  }
  showCommunity(): void {
    this.deps.flow.showCommunity?.();
  }

  back(): void {
    this.screen = 'main';
    this.deps.onChange();
  }

  /** Launch the mission at row `index` through the launch seam; on game over,
   *  drop back to the mission list (the canvas shell's PlaySinglePlayer loop). */
  launch(index: number): void {
    const row = this.rows[index];
    if (row === undefined) return;
    this.launchMission(row.miid);
  }

  /** Launch a mission that isn't a row of the current list — the add-on pack catalog's
   *  PLAY (its freshly downloaded pack's first mission). Going through the controller is
   *  what marks the menu inGame so render() hides it; launching on the host directly left
   *  the menu drawn over the running game. On game over, show the (re-enumerated, so the
   *  new pack is listed) mission list. */
  launchMission(miid: MissionIdentifier): void {
    if (this.inGame) return;
    this.inGame = true;
    this.deps.onChange();
    this.deps.flow.playLevel(miid, 0, () => {
      this.inGame = false;
      this.rows = this.enumerateMissions();
      this.screen = 'missions';
      this.deps.onChange();
    });
  }

  openUrl(url: string): void {
    this.deps.openUrl?.(url);
  }

  // ── options (difficulty + sound mute; the canvas InGameOptionsForm subset) ──
  hasOptions(): boolean {
    return this.deps.options !== undefined;
  }
  options(): void {
    const o = this.deps.options;
    if (o === undefined) return;
    // snapshot the live values so CANCEL can roll back (the canvas commits on OK)
    this.optionsSnapshot = {
      difficulty: o.difficulty(),
      muted: o.muted(),
      gameSpeed: o.gameSpeedNotch(),
      scrollSpeed: o.scrollSpeed(),
      mouseMode: o.mouseMode(),
    };
    this.screen = 'options';
    this.deps.onChange();
  }
  difficulty(): Difficulty {
    return this.deps.options?.difficulty() ?? 'normal';
  }
  setDifficulty(d: Difficulty): void {
    this.deps.options?.setDifficulty(d);
    this.deps.onChange();
  }
  muted(): boolean {
    return this.deps.options?.muted() ?? false;
  }
  setMuted(f: boolean): void {
    this.deps.options?.setMuted(f);
    this.deps.onChange();
  }
  mouseMode(): boolean {
    return this.deps.options?.mouseMode() ?? false;
  }
  setMouseMode(f: boolean): void {
    this.deps.options?.setMouseMode(f);
    this.deps.onChange();
  }
  gameSpeedNotch(): number {
    return this.deps.options?.gameSpeedNotch() ?? 0;
  }
  gameSpeedNotchMax(): number {
    return this.deps.options?.gameSpeedNotchMax() ?? 0;
  }
  defaultGameSpeedNotch(): number {
    return this.deps.options?.defaultGameSpeedNotch() ?? 0;
  }
  // slider setters are QUIET (no onChange): the slider updates its own thumb +
  // value label during a drag, so a re-render mid-drag (which would recreate the
  // track and drop the pointer capture) must not fire.
  setGameSpeedNotch(i: number): void {
    this.deps.options?.setGameSpeedNotch(i);
  }
  gameSpeedLabel(): string {
    return this.deps.options?.gameSpeedLabel() ?? '';
  }
  scrollSpeed(): number {
    return this.deps.options?.scrollSpeed() ?? 1;
  }
  setScrollSpeed(v: number): void {
    this.deps.options?.setScrollSpeed(v);
  }
  scrollSpeedLabel(): string {
    return this.deps.options?.scrollSpeedLabel() ?? '';
  }
  /** OK: persist the options and return to the main menu (the form's OK). */
  saveOptions(): void {
    this.deps.options?.save();
    this.optionsSnapshot = null;
    this.back();
  }
  /** CANCEL: revert ALL live option edits (NOT persisted) and return. */
  cancelOptions(): void {
    const o = this.deps.options,
      s = this.optionsSnapshot;
    if (s !== null && o !== undefined) {
      o.setDifficulty(s.difficulty);
      o.setMuted(s.muted);
      o.setGameSpeedNotch(s.gameSpeed);
      o.setScrollSpeed(s.scrollSpeed);
      o.setMouseMode(s.mouseMode);
    }
    this.optionsSnapshot = null;
    this.back();
  }

  // ── load saved game (the canvas LoadGameForm; launchSavedGame seam) ─────────
  hasSaves(): boolean {
    return this.deps.saves !== undefined;
  }
  load(): void {
    if (this.deps.saves === undefined) return;
    this.savedRows = this.deps.saves.list();
    this.screen = 'load';
    this.deps.onChange();
  }
  savedGames(): readonly SaveRow[] {
    return this.savedRows;
  }

  /** Refresh-to-resume: re-enter gameplay from the auto-resume slot's already-opened
   *  stream (knGameReinitializeSave / slot -3 — the WASM client's "Reinitialize"
   *  autosave, excluded from the user-visible LOAD list). Mirrors the canvas
   *  Shell::Launch slot-(-3) arm (shell.ts:259-274): launch the save AS gameplay
   *  BEFORE the title shows, so a page refresh during a mission drops back in-game.
   *  On game over it returns to the main menu, exactly like loadSlot. Single-player
   *  only — the host autosaves the resume slot for SP games only (gamesession.ts:1467),
   *  so this stream only ever exists for an SP mission. */
  resume(pstm: Stream): void {
    if (this.inGame) return;
    this.inGame = true;
    this.deps.onChange();
    this.deps.flow.playSavedGame(pstm, () => {
      this.inGame = false;
      this.screen = 'main';
      this.deps.onChange();
    });
  }

  /** Open the slot and resume it through the launch seam; on game over, return
   *  to the main menu (the canvas PlaySavedGame returns to the shell loop). */
  loadSlot(slot: number): void {
    if (this.deps.saves === undefined || this.inGame) return;
    const pstm = this.deps.saves.open(slot);
    if (pstm === null) return;
    this.inGame = true;
    this.deps.onChange();
    this.deps.flow.playSavedGame(pstm, () => {
      this.inGame = false;
      this.screen = 'main';
      this.deps.onChange();
    });
  }

  // ── help (the menu manual) ──────────────────────────────────────────────────
  // HELP is enabled when EITHER source is present: the full RAW manual (loadHelpRaw →
  // the rich HelpManualView) or the flat tag-stripped text (loadHelp → the legacy text).
  hasHelp(): boolean {
    return this.deps.loadHelpRaw !== undefined || this.deps.loadHelp !== undefined;
  }
  help(): void {
    if (!this.hasHelp()) return;
    this.helpRawText = this.deps.loadHelpRaw?.() ?? '';
    this.helpText = this.deps.loadHelp?.() ?? '';
    this.screen = 'help';
    this.deps.onChange();
  }
  /** the flat tag-stripped text (the fallback rendering / the automation helpText readout). */
  helpContent(): string {
    return this.helpText;
  }
  /** the RAW help.txt (tags intact) for the full-fidelity manual — empty when only the
   *  flat source is wired (the view then falls back to helpContent()). */
  helpRaw(): string {
    return this.helpRawText;
  }
  /** the host's <IMG> resolver (pakr+tam → 'help'-strip frame data URL), or undefined. */
  resolveHelpImage(src: string): string | null {
    return this.deps.resolveHelpImage?.(src) ?? null;
  }
  /** true when the full-fidelity manual can render (a RAW source is wired). */
  hasHelpManual(): boolean {
    return this.deps.loadHelpRaw !== undefined;
  }

  // missionlist.cpp enumeration, read as plain data (no ListControl): the same
  // CreateMissionList the canvas SelectMissionForm builds, single-player filter.
  private enumerateMissions(): MissionRow[] {
    const pml = CreateMissionList(this.deps.mlDeps, null, kmltSinglePlayer);
    if (pml === null) return [];
    const out: MissionRow[] = [];
    const n = pml.GetCount();
    for (let i = 0; i < n; i++) {
      const md = pml.GetMissionDescription(i);
      const miid = pml.GetMissionIdentifier(i);
      if (md === null || miid === null) continue;
      out.push({
        index: out.length, // slot in this list — launch() indexes rows[], not the MissionList
        lvl: miid.szLvlFilename,
        packId: miid.packid.id >>> 0,
        title: md.szLvlTitle,
        pack: md.szPackName,
        author: md.szPackAuthor,
        bucket: bucketOf(md.mt),
        miid,
      });
    }
    return out;
  }
}

/** Convert help.txt tag markup to plain text for the DOM help view. The canvas
 *  HelpControl lays the same tags out richly; here we strip to readable text
 *  (headers/breaks become newlines; images and link wrappers are dropped).
 *  The result is PLAIN TEXT — render it via textContent, never innerHTML. */
export function stripHelpTags(raw: string): string {
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<hr\s*\/?>/gi, '\n--------\n')
    .replace(/<\/?h[12][^>]*>/gi, '\n')
    .replace(/<img[^>]*>/gi, '')
    .replace(/<\/?a[^>]*>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
